import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  MouseSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { Empty, Modal } from 'antd';
import type { FC } from 'react';
import React, { useRef, useState } from 'react';

import { PictureComponent } from '../../PictureComponent/PictureComponent';
import type { Picture } from '../Types';

import { UploadItem } from './UploadItem/UploadItem';
import './UploadList.scss';

/**
 * @deprecated Use PictureList from 'app/components/commons/ImageUploader/PictureList/PictureList' instead.
 * Migration guide: Replace SortableUploadList with PictureList from ImageUploader.
 */
export const UploadList: FC<IProps> = ({
  pictures,
  uploadingItems,
  remove,
  edit,
  customButton = null,
  previewable = false,
  draggable = false,
  fromUploader = false,
  disabled = false,
  onChange,
}) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const dndIds = useRef(new WeakMap<Picture, string>());
  const dndIdCounter = useRef(0);

  const getPictureDndId = (pic: Picture) => {
    let dndId = dndIds.current.get(pic);

    if (!dndId) {
      dndIdCounter.current += 1;
      dndId = `dnd-${dndIdCounter.current}`;
      dndIds.current.set(pic, dndId);
    }

    return dndId;
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 4 },
    })
  );

  const showPreview = (pic: Picture) => {
    setPreviewTitle(pic.name);
    setPreviewImage(pic.pictureId);
    setPreviewVisible(true);
  };

  const closePreview = () => {
    setPreviewVisible(false);
    setPreviewTitle('');
    setPreviewImage('');
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!pictures || !onChange || !over || active.id === over.id) {
      return;
    }

    const oldIndex = pictures.findIndex(
      (pic) => getPictureDndId(pic) === active.id
    );
    const newIndex = pictures.findIndex(
      (pic) => getPictureDndId(pic) === over.id
    );

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    onChange(arrayMove(pictures, oldIndex, newIndex));
  };

  const renderItems = () =>
    pictures?.map((pic) => (
      <UploadItem
        key={getPictureDndId(pic)}
        dndId={draggable ? getPictureDndId(pic) : undefined}
        item={pic}
        draggable={draggable}
        onPreview={previewable ? () => showPreview(pic) : undefined}
        onRemove={
          remove ? () => remove(`${pic.pictureId}-${pic.id}`) : undefined
        }
        onEdit={edit && !disabled ? () => edit(pic) : undefined}
      />
    ));

  return (
    <div className="upload-list">
      {draggable && pictures ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={pictures.map(getPictureDndId)}
            strategy={rectSortingStrategy}
          >
            {renderItems()}
          </SortableContext>
        </DndContext>
      ) : (
        renderItems()
      )}
      {uploadingItems &&
        Object.keys(uploadingItems).map((key) => (
          <div className="upload-list__uploading-item" key={key}>
            <span>Uploading ...</span>
          </div>
        ))}
      {!disabled && <div className="upload-list__new-item">{customButton}</div>}
      {!pictures?.length && !customButton && !fromUploader && !disabled && (
        <div className="upload-list__empty-list">
          <Empty />
        </div>
      )}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={closePreview}
        wrapClassName="upload-list-modal"
      >
        <div className="thumbnail">
          <PictureComponent pictureId={previewImage} alt="alt" width={472} />
        </div>
      </Modal>
    </div>
  );
};

export const SortableUploadList: FC<ISortableProps> = (props) => (
  <UploadList {...props} draggable />
);

type IProps = {
  pictures: Picture[];
  uploadingItems: {
    [fileName: string]: number;
  };
  remove?: (uid: string) => void;
  edit?: (picture: Picture) => void;
  customButton?: React.ReactNode;
  previewable?: boolean;
  draggable?: boolean;
  fromUploader?: boolean;
  disabled?: boolean;
  onChange?: (pictures: Picture[]) => void;
};

type ISortableProps = IProps;
